import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Button } from './Button';
import { Input } from './Input';

export interface TableColumn<T> {
  key: string;
  label: string | React.ReactNode;
  sortable?: boolean;
  render?: (row: T) => React.ReactNode;
  headerClassName?: string;
  cellClassName?: string;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  isLoading?: boolean;
  enableSearch?: boolean;
  enablePagination?: boolean;
  pageSize?: number;
  searchPlaceholder?: string;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  filters?: React.ReactNode;
  showHeader?: boolean;
  onSearch?: (value: string) => void;
}

export function Table<T extends Record<string, any>>({
  columns,
  data,
  isLoading = false,
  enableSearch = true,
  enablePagination = true,
  pageSize = 10,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No data available',
  onRowClick,
  filters,
  showHeader = true,
  onSearch,
}: TableProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' });

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!searchQuery) return data;

    return data.filter(row =>
      Object.values(row as Record<string, any>)
        .join(' ')
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  }, [data, searchQuery]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key!];
      const bValue = b[sortConfig.key!];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!enablePagination) return sortedData;

    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize, enablePagination]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const renderPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (isLoading) {
    return (
      <div className='w-full'>
        {showHeader && (
          <div className='mb-4 flex items-center gap-4'>
            {enableSearch && (
              <div className='relative flex-1'>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
                <Input type='text' placeholder={searchPlaceholder} value='' className='pl-10' disabled onChange={() => {}} />
              </div>
            )}
            {filters && <div>{filters}</div>}
          </div>
        )}
        <div className='flex h-64 items-center justify-center rounded-lg border border-slate-100 bg-white'>
          <div className='text-center'>
            <div className='mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-primary'></div>
            <p className='mt-2 text-sm text-slate-500'>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='w-full'>
      {/* Header with search and filters */}
      {showHeader && (
        <div className='mb-4 flex flex-col gap-4 sm:flex-row sm:items-center'>
          {enableSearch && (
            <div className='relative flex-1'>
              <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
              <Input
                type='text'
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={e => {
                  onSearch ? onSearch(e.target.value) : setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className='pl-10'
              />
            </div>
          )}
          {filters && <div className='flex items-center gap-2'>{filters}</div>}
        </div>
      )}

      {/* Table */}
      <div className='overflow-hidden rounded-lg border border-slate-100 bg-white'>
        <div className='overflow-x-auto'>
          <table className='min-w-full'>
            <thead>
              <tr className='border-b border-slate-100'>
                {columns.map(column => (
                  <th
                    key={column.key}
                    className={`px-4 py-3 text-left text-sm font-medium text-slate-700 ${
                      column.sortable ? 'cursor-pointer select-none hover:bg-slate-50' : ''
                    } ${column.headerClassName || ''}`}
                    onClick={() => column.sortable && handleSort(column.key)}
                  >
                    <div className='flex items-center gap-2'>
                      {column.label}
                      {column.sortable && sortConfig.key === column.key && (
                        <span className='text-primary'>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className='px-4 py-12 text-center text-sm text-slate-500'>
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={`${
                      rowIndex % 2 === 1 ? 'bg-slate-50' : ''
                    } ${onRowClick ? 'cursor-pointer hover:bg-secondary/30 transition-colors' : ''}`}
                    onClick={() => onRowClick?.(row)}
                  >
                    {columns.map(column => (
                      <td
                        key={column.key}
                        className={`whitespace-nowrap px-4 py-2 border-b border-slate-100 text-sm text-slate-900 ${column.cellClassName || ''}`}
                      >
                        {column.render ? column.render(row) : row[column.key]?.toString() || '-'}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {enablePagination && sortedData.length > 0 && (
        <div className='mt-4 flex items-center justify-between bg-white px-4 py-3 sm:px-6 rounded-lg'>
          <div className='flex flex-1 justify-between sm:hidden'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <Button
              variant='outline'
              size='sm'
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
          <div className='hidden sm:flex sm:flex-1 sm:items-center sm:justify-between'>
            <div>
              <p className='text-sm text-slate-600'>
                Showing <span className='font-medium text-slate-900'>{(currentPage - 1) * pageSize + 1}</span> to{' '}
                <span className='font-medium text-slate-900'>{Math.min(currentPage * pageSize, sortedData.length)}</span>{' '}
                of <span className='font-medium text-slate-900'>{sortedData.length}</span> results
              </p>
            </div>
            <div>
              <nav className='isolate inline-flex -space-x-px rounded-md'>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className='relative inline-flex items-center rounded-l-md px-2 py-2 text-slate-400 hover:bg-slate-50 focus:z-20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                >
                  <ChevronLeft className='h-5 w-5' />
                </button>
                {renderPageNumbers().map((page, index) =>
                  page === '...' ? (
                    <span
                      key={`ellipsis-${index}`}
                      className='relative inline-flex items-center px-4 py-2 text-sm font-semibold text-slate-700'
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page as number)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold transition-colors ${
                        currentPage === page ? 'z-10 bg-primary text-white' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className='relative inline-flex items-center rounded-r-md px-2 py-2 text-slate-400 hover:bg-slate-50 focus:z-20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                >
                  <ChevronRight className='h-5 w-5' />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
